import { BrowserAuthErrorCodes } from '@azure/msal-browser';
import { acquireAccessToken, entraLogout } from 'Components/Auth';
import { RootState } from 'Data/Redux';
import { hasValue } from 'Lib/Any';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, merge, of } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap } from 'rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { sanitizeErrorObject } from '../../Helpers/errorHelpers';
import { addErrorNotice, callApi, camelizeObjectKeys, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import * as rootActions from '../../Redux/actions';
import { actions as noticeActions } from '../Notices';
import { actions as userActions } from '../User';
import { actions } from './slice';

export const checkAuth: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.checkLogin.toString()),
    mergeMap(() => {
      const state: RootState = state$.value;
      if (state.authentication.isAuthenticated) return of(userActions.getUser());

      if (
        process.env.REACT_APP_AUTOLOGIN === 'true' &&
        !!process.env.REACT_APP_AUTH_USERNAME &&
        !!process.env.REACT_APP_AUTH_PASSWORD
      )
        return of(
          actions.postLogin({
            username: process.env.REACT_APP_AUTH_USERNAME,
            password: process.env.REACT_APP_AUTH_PASSWORD,
          })
        );

      return EMPTY;
    })
  );

export const login: Epic = (action$) =>
  action$.pipe(
    ofType(actions.postLogin.toString()),
    mergeMap(({ payload: { username: Name, password: Password, token: Token } }) =>
      callApi(webApi.auth.post({ Body: { Name, Password, Token } })).pipe(
        mergeMap((res) => {
          //Action that tells the App to look for the JWT Cookie and login
          if (window.qest.userAgent?.includes('[QF.App]'))
            (window['jsBridge' as any] as any).invokeAction('1', 'logIn');

          //Handle login success if in iframe - QF/QFS
          if (window.qest.isFrame && window.parent.QP.LoginSuccessful) window.parent.QP.LoginSuccessful();

          //Login success within QL
          if (window.qest.userAgent?.includes('[QL]')) document.body.dispatchEvent(new CustomEvent('loginSuccessful'));

          return of(actions.postLoginResolve(res.Body), userActions.getUser());
        }),
        catchError((err) => of(actions.postLoginReject(sanitizeErrorObject(err))))
      )
    )
  );

export const logout: Epic = (action$) =>
  action$.pipe(
    ofType(actions.postLogout.toString()),
    mergeMap(() =>
      merge(
        of(rootActions.clearState()),
        callApi(webApi.auth.revoke.post({ Body: {} })).pipe(
          ignoreElements(),
          catchError((err) => of(addErrorNotice('Failed to log out from server.', err)))
        )
      )
    )
  );

export const handleUnauthenticatedResponses: Epic = (action$) =>
  action$.pipe(
    ofType(actions.requestFailure.toString()),
    filter((action) => action.statusCode === 401),
    map(() => actions.postLogout())
  );

export const checkDomainWarnings: Epic = (action$) =>
  action$.pipe(
    ofType(actions.checkLogin.toString()),
    mergeMap(() => {
      const worksheetsHostname = window.location.hostname.toLowerCase();
      const webapiHostname = new URL(process.env.REACT_APP_WEB_API_URL || window.location.href).hostname;
      if (
        worksheetsHostname !== webapiHostname &&
        (worksheetsHostname === 'localhost' || webapiHostname === 'localhost')
      ) {
        return of(
          noticeActions.addNotice({
            message:
              `You appear to be using '${worksheetsHostname}' for worksheets and '${webapiHostname}' for web api. ` +
              `This can have unpredictable results, you may need to reconfigure your worksheets app.`,
            severity: 'error',
          })
        );
      }
      return EMPTY;
    })
  );

export const getPlatformVersion: Epic = (action$) =>
  action$.pipe(
    ofType(actions.getPlatformVersion.toString()),
    mergeMap(() =>
      callApi(webApi.env.get()).pipe(
        map(
          ({
            Body: {
              QestPlatformVersion: { Version },
            },
          }) => actions.getPlatformVersionResolve(Version)
        ),
        catchWithAuthErrors((err: any) =>
          of(actions.getPlatformVersionReject(), addErrorNotice('Failed to retrieve QEST Platform version.', err))
        )
      )
    )
  );

export const getSettings: Epic = (action$) =>
  action$.pipe(
    ofType(actions.getSettings.toString()),
    mergeMap(() =>
      callApi(webApi.auth.settings.get()).pipe(
        map(({ Body }) => actions.getSettingsResolve(camelizeObjectKeys(Body))),
        catchWithAuthErrors((err: any) =>
          of(actions.getSettingsReject(), addErrorNotice('Failed to retrieve authentication settings.', err))
        )
      )
    )
  );

export const postEntraLogin: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.postEntraLogin.toString()),
    mergeMap(() => {
      const state: RootState = state$.value;
      const settings = state.authentication.authSettings;

      if (!settings?.clientId || !settings?.tenantId)
        return of(
          actions.postEntraLoginReject({
            severity: 'error',
            message: 'Microsoft Entra Authentication is enabled but the client ID or tenant ID is null.',
          })
        );
      if (!state.authentication.entra.isInitialised)
        return of(
          actions.postEntraLoginReject({
            message: 'Microsoft Entra Authentication is not initialised',
            severity: 'error',
          })
        );

      return from(acquireAccessToken(settings?.clientId!)).pipe(
        mergeMap((res: any) => {
          if (hasValue(res?.accessToken))
            return of(actions.postLogin({ token: res?.accessToken }), actions.postEntraLoginResolve());
          return of(actions.postEntraLoginResolve());
        }),
        catchError((err: any) => {
          switch (err?.errorCode) {
            case BrowserAuthErrorCodes.popupWindowError:
              return of(
                actions.postEntraLoginReject({
                  message: 'Please check if pop ups are disabled if logging in using a browser',
                  severity: 'warning',
                })
              );
            case BrowserAuthErrorCodes.userCancelled:
              return of(actions.postEntraLoginResolve());
            default:
              return of(
                actions.postEntraLoginReject({
                  message: `Failed to log in using Microsoft Entra. Error: ${err.message}`,
                  severity: 'error',
                })
              );
          }
        })
      );
    })
  );

export const postEntralogout: Epic = (action$) =>
  action$.pipe(
    ofType(actions.postEntraLogout.toString()),
    mergeMap(() => from(entraLogout())),
    ignoreElements()
  );
